import { useState, useEffect, useRef } from 'react';
import {
  Mail, Phone, MapPin, Instagram, Facebook, Twitter, Youtube,
  Linkedin, Music, Calendar, ExternalLink, Play, ChevronRight, ChevronLeft, Star, Quote,
  User, ArrowRight, X, Plus, Briefcase, Users, HelpCircle, MessageSquare, CheckCircle,
  Pause
} from 'lucide-react';

import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { AnimatedSection } from './AnimatedSection';

const THEMES = {
  dark_stage: {
    bg: 'bg-slate-900',
    text: 'text-white',
    textMuted: 'text-slate-400',
    card: 'bg-slate-800',
    border: 'border-slate-700',
    accent: 'text-orange-500',
    button: 'bg-orange-500 hover:bg-orange-400',
  },
  theatre_red: {
    bg: 'bg-[#1a0c0e] tx-velvet',
    text: 'text-[#f8f0e6]',
    textMuted: 'text-[#bfa39a]',
    card: 'bg-[#120709] tx-velvet-alt',
    border: 'border-[#3b2125]',
    accent: 'text-[#d4af37]',
    button: 'bg-[#b91c1c] hover:bg-[#dc2626]',
  },
  ivory_gold: {
    bg: 'bg-[#faf7f1]',
    text: 'text-[#1c1710]',
    textMuted: 'text-[#8d7f68]',
    card: 'bg-[#f1ebdd]',
    border: 'border-[#e2d8c3]',
    accent: 'text-[#b08d2f]',
    button: 'bg-[#1c1710] hover:bg-[#3a2f1e]',
  },
  light_modern: {
    bg: 'bg-white',
    text: 'text-slate-900',
    textMuted: 'text-slate-500',
    card: 'bg-slate-50',
    border: 'border-slate-200',
    accent: 'text-blue-600',
    button: 'bg-blue-600 hover:bg-blue-500',
  },
  colorful_kids: {
    bg: 'bg-amber-50',
    text: 'text-purple-900',
    textMuted: 'text-purple-600',
    card: 'bg-white',
    border: 'border-pink-200',
    accent: 'text-pink-500',
    button: 'bg-pink-500 hover:bg-pink-400',
  },
  elegant_classic: {
    bg: 'bg-stone-900',
    text: 'text-stone-50',
    textMuted: 'text-stone-400',
    card: 'bg-stone-800',
    border: 'border-stone-700',
    accent: 'text-amber-400',
    button: 'bg-amber-500 hover:bg-amber-400',
  },
  bold_artist: {
    bg: 'bg-zinc-900',
    text: 'text-white',
    textMuted: 'text-zinc-400',
    card: 'bg-zinc-800',
    border: 'border-zinc-700',
    accent: 'text-red-500',
    button: 'bg-red-500 hover:bg-red-400',
  },
};

// Social icon mapper
const getSocialIcon = (platform) => {
  const name = platform.toLowerCase();
  if (name.includes('instagram')) return Instagram;
  if (name.includes('facebook')) return Facebook;
  if (name.includes('twitter') || name.includes('x')) return Twitter;
  if (name.includes('youtube')) return Youtube;
  if (name.includes('linkedin')) return Linkedin;
  if (name.includes('spotify') || name.includes('music')) return Music;
  return ExternalLink;
};


export const TestimonialsSection = ({ data, theme, primaryColor }) => {
  const t = THEMES[theme] || THEMES.dark_stage;
  const testimonials = data?.testimonials || [];
  const layout = data?.layout || 'grid';
  
  if (testimonials.length === 0) return null;

  const renderGridLayout = () => (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {testimonials.map((item, idx) => (
        <AnimatedSection
          key={item.id || idx}
          animation="fadeUp"
          delay={idx * 100}
          className={cn("p-6 rounded-2xl border", t.bg, t.border)}
        >
          <div className="flex items-center gap-3 mb-4">
            {item.image ? (
              <img src={item.image} alt={item.author} className="w-12 h-12 rounded-full object-cover" />
            ) : (
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: primaryColor + '20' }}>
                <User className="w-6 h-6" style={{ color: primaryColor }} />
              </div>
            )}
            <div>
              <h4 className={cn("font-semibold", t.text)}>{item.author || 'Anonymous'}</h4>
              {item.role && <p className={cn("text-sm", t.textMuted)}>{item.role}{item.company ? ` at ${item.company}` : ''}</p>}
            </div>
          </div>
          {item.rating && (
            <div className="flex gap-0.5 mb-3">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className={cn("w-4 h-4", i < item.rating ? "text-amber-400 fill-amber-400" : "text-slate-600")} />
              ))}
            </div>
          )}
          <p className={cn("italic", t.textMuted)}>"{item.quote}"</p>
        </AnimatedSection>
      ))}
    </div>
  );

  const renderMasonryLayout = () => (
    <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
      {testimonials.map((item, idx) => (
        <AnimatedSection
          key={item.id || idx}
          animation="fadeUp"
          delay={idx * 100}
          className={cn("break-inside-avoid p-6 rounded-2xl border", t.bg, t.border)}
        >
          <Quote className="w-8 h-8 mb-4" style={{ color: primaryColor + '40' }} />
          <p className={cn("text-lg mb-4", t.text)}>"{item.quote}"</p>
          {item.rating && (
            <div className="flex gap-0.5 mb-3">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className={cn("w-4 h-4", i < item.rating ? "text-amber-400 fill-amber-400" : "text-slate-600")} />
              ))}
            </div>
          )}
          <div className="flex items-center gap-3">
            {item.image && <img src={item.image} alt={item.author} className="w-10 h-10 rounded-full object-cover" />}
            <div>
              <h4 className={cn("font-semibold text-sm", t.text)}>{item.author}</h4>
              {item.role && <p className={cn("text-xs", t.textMuted)}>{item.role}</p>}
            </div>
          </div>
        </AnimatedSection>
      ))}
    </div>
  );

  return (
    <section id="testimonials" className={cn("py-24 px-4", t.bg)}>
      <div className="max-w-6xl mx-auto">
        <AnimatedSection animation="fadeUp" className="text-center mb-16">
          <span className="text-sm uppercase tracking-widest font-semibold mb-4 block" style={{ color: primaryColor }}>What People Say</span>
          <h2 className={cn("text-4xl md:text-5xl font-bold", t.text)} style={{ fontFamily: "'Outfit', sans-serif" }}>{data.title || 'Testimonials'}</h2>
        </AnimatedSection>
        {layout === 'masonry' ? renderMasonryLayout() : renderGridLayout()}
      </div>
    </section>
  );
};


